// Bridges a plugin's fold provider into the core `foldRegistry`. A plugin
// registers a fold provider over RPC (slate.editor.registerFoldProvider); the
// host hears the registration and calls `add()`, which inserts a core
// `FoldProvider` whose `getFolds` calls back into the plugin (over the same
// channel a tree-data provider uses). The bridge tracks providers per plugin so
// disable / uninstall / worker-unregister can remove them again. No DOM, no host
// power: the plugin only ever returns inert line ranges.

import { foldRegistry as globalFoldRegistry, type FoldProvider, type FoldRange, type FoldRegistry } from '@slate/core';

/** Pulls a plugin provider's folds (implemented by the PluginHost connection). */
export interface FoldFetcher {
  getFolds(providerId: string, source: string, fileName: string): Promise<FoldRange[]>;
}

export class HostFoldRegistry {
  // key (`pluginId::providerId`) -> core-registry disposer.
  private readonly bridges = new Map<string, () => void>();

  constructor(private readonly registry: FoldRegistry = globalFoldRegistry) {}

  private key(pluginId: string, providerId: string): string {
    return `${pluginId}::${providerId}`;
  }

  /** Bridge a plugin fold provider into the core registry. Idempotent: a repeat
   *  registration of the same (plugin, provider) replaces the prior bridge. */
  add(
    pluginId: string,
    providerId: string,
    extensions: readonly string[],
    fetcher: FoldFetcher,
  ): void {
    this.removeProvider(pluginId, providerId);
    const provider: FoldProvider = {
      id: this.key(pluginId, providerId),
      extensions,
      // Route back to the plugin with its ORIGINAL provider id.
      getFolds: (source, fileName) => fetcher.getFolds(providerId, source, fileName),
    };
    const dispose = this.registry.register(provider);
    this.bridges.set(this.key(pluginId, providerId), dispose);
  }

  /** Remove a single bridged provider (worker unregister / dispose). */
  removeProvider(pluginId: string, providerId: string): void {
    const k = this.key(pluginId, providerId);
    const dispose = this.bridges.get(k);
    if (dispose) {
      dispose();
      this.bridges.delete(k);
    }
  }

  /** Remove every bridged provider for a plugin (disable / uninstall). */
  removePlugin(pluginId: string): void {
    const prefix = `${pluginId}::`;
    for (const [k, dispose] of [...this.bridges]) {
      if (k.startsWith(prefix)) {
        dispose();
        this.bridges.delete(k);
      }
    }
  }

  /** Currently bridged keys (`pluginId::providerId`), for introspection / tests. */
  list(): string[] {
    return [...this.bridges.keys()];
  }
}
